import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ApiService } from '../providers';
import { Injectable, inject } from '@angular/core';
import { PasswordPolicies, PasswordPolicy } from '../models/PasswordPoliciesModel';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { catchError, map } from 'rxjs/operators';
import { of, shareReplay } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PasswordValidationService {
  private readonly api = inject(ApiService);

  public policies$ = this.api.restGet<PasswordPolicies>('data/passwordoptions').pipe(
    map((policies) => (policies.passwordPolicies?.length > 0 ? this.mapPolicies(policies) : { ...policies })),
    catchError((error) => {
      console.error('Fehler beim Laden der Passwortrichtlinien:', error);
      return of(null);
    }),
    shareReplay(1),
  );

  /**
   * Validator, der ein Passwort gegen dynamisch geladene {@link PasswordPolicy}-Regeln prüft.
   * Liefert eine Fehlerstruktur mit übersetzbaren Texten bei Verstößen.
   *
   * @param policies Liste von Passwortregeln zur Prüfung
   * @param minLength Minimale erlaubte Länge des Passworts (optional)
   * @param maxLength Maximale erlaubte Länge des Passworts (optional)
   * @returns Angular ValidatorFn, das die Validierung übernimmt
   */
  public createPasswordValidator(policies: PasswordPolicy[], minLength?: number, maxLength?: number): ValidatorFn {
    if (policies.length === 0 && minLength == null && maxLength == null) {
      throw new Error('Es wurden keine Passwortregeln definiert.');
    }
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;
      if (value === null) {
        return null;
      }

      const toValidateRules: string[] = [];

      if (minLength != null && value.length < minLength) {
        toValidateRules.push(_('passwort_meter.rule_description.min_length'));
      }

      if (maxLength != null && value.length > maxLength) {
        toValidateRules.push(_('passwort_meter.rule_description.max_length'));
      }

      for (const policy of policies) {
        const pattern = this.toRegExp(policy.pattern);
        if (!pattern.test(value)) {
          toValidateRules.push(policy.text || policy.name);
        }
      }

      return toValidateRules.length ? { passwordPolicy: { toValidateRules } } : null;
    };
  }

  /**
   * Wandelt rohe Passwortregeln in vollständig angereicherte {@link PasswordPolicy}-Objekte um.
   *
   * Dabei wird das `pattern`-Feld, falls notwendig, in ein {@link RegExp} umgewandelt.
   * Zusätzlich wird ein standardisierter Schlüssel für die Textübersetzung auf Basis des Regel-Namens erzeugt.
   *
   * @param data Das vom Backend gelieferte {@link PasswordPolicies}-Objekt mit rohen Regeln
   * @returns Ein neues {@link PasswordPolicies}-Objekt mit angereicherten {@link PasswordPolicy}-Regeln
   */
  private mapPolicies(data: PasswordPolicies): PasswordPolicies {
    const mappedPolicies: PasswordPolicy[] = data.passwordPolicies.map((policy) => ({
      ...policy,
      pattern: this.toRegExp(policy.pattern),
      text: policy.name
        ? `passwort_meter.rule_description.${policy.name}`
        : 'passwort_meter.description_rules.unknown_rule',
    }));

    return {
      ...data,
      passwordPolicies: mappedPolicies,
    };
  }

  /**
   * Wandelt einen Pattern-Wert sicher in ein RegExp-Objekt um.
   * Falls der Pattern bereits ein RegExp ist, wird er unverändert zurückgegeben.
   *
   * @param pattern Der Pattern-Wert, entweder als RegExp oder als string
   * @returns Ein gültiges RegExp-Objekt
   */
  private toRegExp(pattern: string | RegExp): RegExp {
    return pattern instanceof RegExp ? pattern : new RegExp(pattern);
  }
}
